import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  FormGroup,
  FormControl,
  Button,
  Divider,
  Grid,
  Alert,
  CircularProgress,
  InputLabel,
  Paper,
  IconButton,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Info as InfoIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { notificationApi } from 'src/utils/notificationApi';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';

const NOTIFICATION_TYPES = [
  { name: 'task_assigned', label: 'Task Assigned', description: 'Notify when a task is assigned to you' },
  { name: 'task_due_soon', label: 'Task Due Soon', description: 'Notify when your tasks are due soon' },
  { name: 'task_overdue', label: 'Task Overdue', description: 'Notify when your tasks are overdue' },
  { name: 'task_completed', label: 'Task Completed', description: 'Notify when a task is completed' },
  { name: 'stage_changed', label: 'Stage Changed', description: 'Notify when a company stage is changed' },
  { name: 'company_assigned', label: 'Company Assigned', description: 'Notify when a company is assigned to you' },
  { name: 'company_created', label: 'Company Created', description: 'Notify when a new company is created' },
  { name: 'document_uploaded', label: 'Document Uploaded', description: 'Notify when a document is uploaded' },
  { name: 'meeting_scheduled', label: 'Meeting Scheduled', description: 'Notify when a meeting is scheduled' },
  { name: 'meeting_reminder', label: 'Meeting Reminder', description: 'Remind you of upcoming meetings' },
  { name: 'comment_mention', label: 'Comment Mention', description: 'Notify when you are mentioned in a comment' },
  { name: 'team_member_added', label: 'Team Member Added', description: 'Notify when you are added to a team' },
  { name: 'email_sent', label: 'Email Sent', description: 'Notify when an email is sent through the system' },
  { name: 'ticket_status_updates', label: 'Ticket Status Updates', description: 'Notify when a support ticket status is updated' },
  { name: 'ticket_comment_replies', label: 'Ticket Comment Replies', description: 'Notify when a comment is added to your ticket' },
];

export default function NotificationSettingsPanel({ onBack, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    push_enabled: true,
    email_enabled: true,
    in_app_enabled: true,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00:00',
    quiet_hours_end: '07:00:00',
    task_assigned: true,
    task_due_soon: true,
    task_overdue: true,
    task_completed: true,
    stage_changed: true,
    company_assigned: true,
    company_created: true,
    document_uploaded: false,
    meeting_scheduled: true,
    meeting_reminder: true,
    comment_mention: true,
    team_member_added: true,
    email_sent: false,
    ticket_status_updates: true,
    ticket_comment_replies: true,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await notificationApi.getSettings();
      const apiSettings = response.data || response;
      
      setSettings({
        push_enabled: apiSettings.push_enabled,
        email_enabled: apiSettings.email_enabled,
        in_app_enabled: apiSettings.in_app_enabled,
        quiet_hours_enabled: apiSettings.quiet_hours_enabled,
        quiet_hours_start: apiSettings.quiet_hours_start || '22:00:00',
        quiet_hours_end: apiSettings.quiet_hours_end || '07:00:00',
        task_assigned: apiSettings.task_assigned,
        task_due_soon: apiSettings.task_due_soon,
        task_overdue: apiSettings.task_overdue,
        task_completed: apiSettings.task_completed,
        stage_changed: apiSettings.stage_changed,
        company_assigned: apiSettings.company_assigned,
        company_created: apiSettings.company_created,
        document_uploaded: apiSettings.document_uploaded,
        meeting_scheduled: apiSettings.meeting_scheduled,
        meeting_reminder: apiSettings.meeting_reminder,
        comment_mention: apiSettings.comment_mention,
        team_member_added: apiSettings.team_member_added,
        email_sent: apiSettings.email_sent,
        ticket_status_updates: apiSettings.ticket_status_updates,
        ticket_comment_replies: apiSettings.ticket_comment_replies,
      });
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      showErrorToast('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (setting) => (event) => {
    setSettings({
      ...settings,
      [setting]: event.target.checked,
    });
  };

  const handleQuietHoursChange = (field) => (event) => {
    setSettings({
      ...settings,
      [field]: event.target.value,
    });
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await notificationApi.updateSettings(settings);
      showToast('Notification settings saved successfully', 'success');
      
      // Refetch to confirm the save
      await fetchSettings();
    } catch (error) {
      console.error('Error saving notification settings:', error);
      showErrorToast('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleEnableAll = () => {
    setSettings({
      ...settings,
      task_assigned: true,
      task_due_soon: true,
      task_overdue: true,
      task_completed: true,
      stage_changed: true,
      company_assigned: true,
      company_created: true,
      document_uploaded: true,
      meeting_scheduled: true,
      meeting_reminder: true,
      comment_mention: true,
      team_member_added: true,
      email_sent: true,
      ticket_status_updates: true,
      ticket_comment_replies: true,
    });
  };

  const handleDisableAll = () => {
    setSettings({
      ...settings,
      task_assigned: false,
      task_due_soon: false,
      task_overdue: false,
      task_completed: false,
      stage_changed: false,
      company_assigned: false,
      company_created: false,
      document_uploaded: false,
      meeting_scheduled: false,
      meeting_reminder: false,
      comment_mention: false,
      team_member_added: false,
      email_sent: false,
      ticket_status_updates: false,
      ticket_comment_replies: false,
    });
  };

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Settings
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving || loading}
            size="small"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Global Settings */}
            <Card>
              <CardHeader title="Global Settings" titleTypographyProps={{ variant: 'subtitle1' }} />
              <CardContent>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.in_app_enabled}
                        onChange={handleToggle('in_app_enabled')}
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">In-app Notifications</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.push_enabled}
                        onChange={handleToggle('push_enabled')}
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Push Notifications</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.email_enabled}
                        onChange={handleToggle('email_enabled')}
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Email Notifications</Typography>}
                  />
                </FormGroup>
              </CardContent>
            </Card>

            {/* Quiet Hours */}
            <Card>
              <CardHeader 
                title="Quiet Hours"
                titleTypographyProps={{ variant: 'subtitle1' }}
              />
              <CardContent>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.quiet_hours_enabled}
                        onChange={handleToggle('quiet_hours_enabled')}
                        color="primary"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Enable Quiet Hours</Typography>}
                  />
                </FormGroup>
                
                {settings.quiet_hours_enabled && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Start</InputLabel>
                          <Select
                            value={settings.quiet_hours_start || '22:00:00'}
                            onChange={handleQuietHoursChange('quiet_hours_start')}
                            label="Start"
                          >
                            {Array.from({ length: 24 }, (_, hour) => (
                              <MenuItem key={hour} value={`${hour.toString().padStart(2, '0')}:00:00`}>
                                {hour === 0 ? '12 AM' : 
                                 hour < 12 ? `${hour} AM` : 
                                 hour === 12 ? '12 PM' : 
                                 `${hour - 12} PM`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={2} sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          to
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>End</InputLabel>
                          <Select
                            value={settings.quiet_hours_end || '07:00:00'}
                            onChange={handleQuietHoursChange('quiet_hours_end')}
                            label="End"
                          >
                            {Array.from({ length: 24 }, (_, hour) => (
                              <MenuItem key={hour} value={`${hour.toString().padStart(2, '0')}:00:00`}>
                                {hour === 0 ? '12 AM' : 
                                 hour < 12 ? `${hour} AM` : 
                                 hour === 12 ? '12 PM' : 
                                 `${hour - 12} PM`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                    
                    <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
                      <Typography variant="caption">No push notifications during this time</Typography>
                    </Alert>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Notification Types */}
            <Card>
              <CardHeader 
                title="Notification Types" 
                titleTypographyProps={{ variant: 'subtitle1' }}
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={handleEnableAll}
                    >
                      Enable All
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small"
                      color="error"
                      onClick={handleDisableAll}
                    >
                      Disable All
                    </Button>
                  </Box>
                }
              />
              <CardContent>
                <Grid container spacing={1.5}>
                  {NOTIFICATION_TYPES.map((type) => (
                    <Grid item xs={12} key={type.name}>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 1.5,
                          backgroundColor: settings[type.name] ? 'action.selected' : 'background.paper',
                          borderColor: settings[type.name] ? 'primary.light' : 'divider'
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings[type.name]}
                              onChange={handleToggle(type.name)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={<Typography variant="body2" sx={{ fontWeight: 500 }}>{type.label}</Typography>}
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                          {type.description}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </>
  );
}
